import { useEffect, useState, useCallback } from "react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { confirmRemove } from "@/lib/confirm";
import { toast } from "sonner";
import { Trash2, Pencil, FileText, ExternalLink, X } from "lucide-react";

const BACKEND = API_BASE.replace(/\/api$/, "");
const resolve = (link) => (link && link.startsWith("/api/") ? `${BACKEND}${link}` : link);
const EMPTY = { title: "", description: "", positions: "", status: "open", posted_at: "", apply_url: "", notification_url: "", corrigendum_url: "" };
const inputCls = "w-full bg-white border border-ink/15 focus:border-teal outline-none px-3 py-2 text-ink text-sm";

export default function RecruitmentsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [files, setFiles] = useState({ notification_pdf: null, corrigendum_pdf: null });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => api.get("/recruitments").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { refresh(); }, [refresh]);

  const startEdit = (n) => {
    setEditing(n.id);
    setForm({
      title: n.title, description: n.description, positions: (n.positions || []).join("\n"), status: n.status,
      posted_at: n.posted_at || "", apply_url: n.apply_url || "", notification_url: n.notification_url || "", corrigendum_url: n.corrigendum_url || "",
    });
    setFiles({ notification_pdf: null, corrigendum_pdf: null });
  };
  const reset = () => { setEditing(null); setForm(EMPTY); setFiles({ notification_pdf: null, corrigendum_pdf: null }); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (files.notification_pdf) fd.append("notification_pdf", files.notification_pdf);
      if (files.corrigendum_pdf) fd.append("corrigendum_pdf", files.corrigendum_pdf);
      if (editing) await api.put(`/recruitments/${editing}`, fd);
      else await api.post("/recruitments", fd);
      toast.success(editing ? "Notification updated" : "Notification published");
      reset();
      refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (id) => { if (!confirmRemove("this recruitment notice")) return; await api.delete(`/recruitments/${id}`); refresh(); };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <form onSubmit={submit} className="lg:col-span-2 border border-ink/10 bg-surface p-6 space-y-3" data-testid="recruitment-form">
        <div className="flex items-center justify-between">
          <div className="tag">{editing ? "// edit notification" : "// new notification"}</div>
          {editing && <button type="button" onClick={reset} data-testid="recruitment-cancel-edit" className="text-ink/60 hover:text-ink" aria-label="Cancel edit"><X className="w-4 h-4" /></button>}
        </div>
        <L label="Title"><input data-testid="rec-input-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} /></L>
        <L label="Description"><textarea data-testid="rec-input-description" required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} resize-none`} /></L>
        <L label="Posts (one per line)"><textarea data-testid="rec-input-positions" rows={4} value={form.positions} onChange={(e) => setForm({ ...form, positions: e.target.value })} className={`${inputCls} resize-none`} /></L>
        <div className="grid grid-cols-2 gap-3">
          <L label="Status">
            <select data-testid="rec-input-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
              <option value="open">Open</option><option value="closed">Closed</option>
            </select>
          </L>
          <L label="Published date"><input data-testid="rec-input-date" type="date" value={form.posted_at} onChange={(e) => setForm({ ...form, posted_at: e.target.value })} className={inputCls} /></L>
        </div>
        <L label="Apply Here link (Google Form etc.)"><input data-testid="rec-input-apply" value={form.apply_url} onChange={(e) => setForm({ ...form, apply_url: e.target.value })} className={inputCls} placeholder="https://forms.gle/…" /></L>
        <L label="Notification PDF (upload)"><input data-testid="rec-input-notification-pdf" type="file" accept="application/pdf" onChange={(e) => setFiles({ ...files, notification_pdf: e.target.files?.[0] || null })} className="text-xs text-ink/70" /></L>
        <L label="…or Notification URL"><input data-testid="rec-input-notification-url" value={form.notification_url} onChange={(e) => setForm({ ...form, notification_url: e.target.value })} className={inputCls} placeholder="https://" /></L>
        <L label="Corrigendum PDF (upload)"><input data-testid="rec-input-corrigendum-pdf" type="file" accept="application/pdf" onChange={(e) => setFiles({ ...files, corrigendum_pdf: e.target.files?.[0] || null })} className="text-xs text-ink/70" /></L>
        <L label="…or Corrigendum URL"><input data-testid="rec-input-corrigendum-url" value={form.corrigendum_url} onChange={(e) => setForm({ ...form, corrigendum_url: e.target.value })} className={inputCls} placeholder="https://" /></L>
        <button type="submit" disabled={busy} data-testid="rec-submit" className="w-full bg-brand hover:bg-brand-dark disabled:opacity-50 text-white py-2.5 text-sm">
          {busy ? "Saving…" : editing ? "Save changes" : "Publish notification"}
        </button>
      </form>

      <div className="lg:col-span-3 border border-ink/10 bg-white divide-y divide-ink/10 max-h-[80vh] overflow-auto" data-testid="recruitment-admin-list">
        {items.map((n) => (
          <div key={n.id} className="p-4 flex items-start justify-between gap-4" data-testid={`rec-row-${n.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest">
                <span className={n.status === "open" ? "text-teal" : "text-ink/50"}>{n.status}</span>
                <span className="text-ink/50">· {n.posted_at}</span>
              </div>
              <div className="font-display font-bold text-brand leading-snug">{n.title}</div>
              <div className="text-xs text-ink/60 mt-1">{(n.positions || []).length} posts</div>
              <div className="flex gap-3 mt-2 text-xs">
                {n.notification_link && <a href={resolve(n.notification_link)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-teal"><FileText className="w-3 h-3" /> Notification</a>}
                {n.corrigendum_link && <a href={resolve(n.corrigendum_link)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-teal"><FileText className="w-3 h-3" /> Corrigendum</a>}
                {n.apply_url && <a href={n.apply_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-teal"><ExternalLink className="w-3 h-3" /> Apply link</a>}
              </div>
            </div>
            <div className="inline-flex gap-3 shrink-0">
              <button data-testid={`rec-edit-${n.id}`} onClick={() => startEdit(n)} className="text-brand hover:text-brand-dark" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`rec-del-${n.id}`} onClick={() => del(n.id)} className="text-red-500 hover:text-red-600" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="p-6 text-ink/60 text-sm">No notifications yet.</div>}
      </div>
    </div>
  );
}

function L({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-mono uppercase tracking-widest text-ink/60 mb-1">{label}</label>
      {children}
    </div>
  );
}
